import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { walletAPI, withdrawalAPI } from '../../lib/api';
import { formatCurrency, formatDateTime } from '../../lib/utils';
import { GlassCard, StatCard, StatusBadge } from '../../components/common/GlassCard';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '../../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { Wallet as WalletIcon, ArrowUpRight, ArrowDownRight, Banknote, AlertCircle } from 'lucide-react';

const Wallet = () => {
  const { user } = useAuth();
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [withdrawForm, setWithdrawForm] = useState({
    amount: '', bank_name: user?.bank_name || '',
    bank_account: user?.bank_account || '', bank_holder_name: user?.full_name || '',
  });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [walletRes, txRes, withdrawRes] = await Promise.all([
        walletAPI.getWallet(), walletAPI.getTransactions(), withdrawalAPI.getWithdrawals(),
      ]);
      setWallet(walletRes.data); setTransactions(txRes.data); setWithdrawals(withdrawRes.data);
    } catch (error) { console.error('Error loading wallet:', error); }
    finally { setLoading(false); }
  };

  const handleWithdraw = async () => {
    if (user?.kyc_status !== 'approved') { toast.error('KYC must be approved'); return; }
    if (!withdrawForm.amount || parseFloat(withdrawForm.amount) <= 0) { toast.error('Enter valid amount'); return; }
    if (parseFloat(withdrawForm.amount) > (wallet?.balance || 0)) { toast.error('Insufficient balance'); return; }
    try {
      await withdrawalAPI.createWithdrawal({ ...withdrawForm, amount: parseFloat(withdrawForm.amount) });
      toast.success('Withdrawal request submitted');
      setShowWithdrawModal(false);
      setWithdrawForm({ amount: '', bank_name: user?.bank_name || '', bank_account: user?.bank_account || '', bank_holder_name: user?.full_name || '' });
      loadData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to create withdrawal'); }
  };

  if (loading) {
    return (<div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#4E50FF]"></div></div>);
  }

  return (
    <div className="space-y-6" data-testid="wallet-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Wallet</h1>
          <p className="text-gray-500 mt-1">Manage your points and withdrawals</p>
        </div>
        <Dialog open={showWithdrawModal} onOpenChange={setShowWithdrawModal}>
          <DialogTrigger asChild>
            <Button className="bg-[#4E50FF] hover:bg-[#3436CC] text-white" disabled={user?.kyc_status !== 'approved'} data-testid="withdraw-btn">
              <Banknote className="w-4 h-4 mr-2" />Withdraw
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Withdraw Funds</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="p-4 rounded-lg bg-gray-50 text-center">
                <p className="text-sm text-gray-500">Available Balance</p>
                <p className="text-2xl font-bold text-[#4E50FF] font-mono">{formatCurrency(wallet?.balance || 0)}</p>
              </div>
              <div><Label>Amount (MYR)</Label><Input type="number" value={withdrawForm.amount} onChange={(e) => setWithdrawForm({ ...withdrawForm, amount: e.target.value })} max={wallet?.balance || 0} data-testid="withdraw-amount" /></div>
              <div><Label>Bank Name</Label><Input value={withdrawForm.bank_name} onChange={(e) => setWithdrawForm({ ...withdrawForm, bank_name: e.target.value })} /></div>
              <div><Label>Bank Account Number</Label><Input value={withdrawForm.bank_account} onChange={(e) => setWithdrawForm({ ...withdrawForm, bank_account: e.target.value })} /></div>
              <div>
                <Label>Account Holder Name</Label>
                <Input value={withdrawForm.bank_holder_name} onChange={(e) => setWithdrawForm({ ...withdrawForm, bank_holder_name: e.target.value })} data-testid="withdraw-holder-name" />
                <p className="text-xs text-gray-400 mt-1">Must match the name on your account{user?.full_name ? `: ${user.full_name}` : ''}</p>
              </div>
              <Button onClick={handleWithdraw} className="w-full bg-[#4E50FF] hover:bg-[#3436CC] text-white" data-testid="submit-withdraw">Submit Withdrawal Request</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {user?.kyc_status !== 'approved' && (
        <GlassCard className="bg-amber-50 border-amber-200">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-amber-500" />
            <div>
              <p className="text-amber-700 font-medium">KYC Required</p>
              <p className="text-sm text-gray-500">Complete your KYC verification to enable withdrawals</p>
            </div>
          </div>
        </GlassCard>
      )}

      <div className="grid md:grid-cols-3 gap-6">
        <GlassCard className="md:col-span-2 border-l-4 border-l-[#4E50FF]">
          <div className="flex items-center gap-4">
            <div className="p-4 rounded-xl bg-[#4E50FF]/10">
              <WalletIcon className="w-8 h-8 text-[#4E50FF]" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Balance</p>
              <p className="text-3xl font-bold text-gray-900 font-mono">{formatCurrency(wallet?.balance || 0)}</p>
              <p className="text-xs text-gray-400">Points (MYR 1 = 1 Point)</p>
            </div>
          </div>
        </GlassCard>
        <StatCard title="Pending Withdrawals" value={withdrawals.filter(w => w.status === 'pending').length} icon={Banknote} />
      </div>

      <Tabs defaultValue="transactions">
        <TabsList className="bg-gray-100/80">
          <TabsTrigger value="transactions">Transactions</TabsTrigger>
          <TabsTrigger value="withdrawals">Withdrawals</TabsTrigger>
        </TabsList>

        <TabsContent value="transactions" className="mt-6">
          {transactions.length > 0 ? (
            <GlassCard className="overflow-hidden p-0">
              <Table>
                <TableHeader>
                  <TableRow className="border-gray-100">
                    <TableHead className="text-gray-500 font-semibold">Type</TableHead>
                    <TableHead className="text-gray-500 font-semibold">Description</TableHead>
                    <TableHead className="text-gray-500 font-semibold">Amount</TableHead>
                    <TableHead className="text-gray-500 font-semibold">Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transactions.map((tx) => (
                    <TableRow key={tx.id} className="border-gray-50">
                      <TableCell>
                        <div className={`flex items-center gap-2 ${tx.type === 'credit' ? 'text-emerald-600' : 'text-red-500'}`}>
                          {tx.type === 'credit' ? <ArrowDownRight className="w-4 h-4" /> : <ArrowUpRight className="w-4 h-4" />}
                          <span className="capitalize">{tx.type}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-gray-700">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span>{tx.description}</span>
                          {tx.category === 'fast_comm_advance' && (
                            <span
                              className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-800 bg-amber-100 border border-amber-200 px-1.5 py-0.5 rounded"
                              data-testid={`fast-comm-badge-${tx.id}`}
                              title="Paid upfront before developer payment"
                            >
                              ⚡ Paid (via Fast Comm)
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className={`font-mono ${tx.type === 'credit' ? 'text-emerald-600' : 'text-red-500'}`}>
                        {tx.type === 'credit' ? '+' : '-'}{formatCurrency(tx.amount)}
                      </TableCell>
                      <TableCell className="text-gray-500">{formatDateTime(tx.created_at)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </GlassCard>
          ) : (
            <GlassCard className="text-center py-12">
              <WalletIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Transactions</h3>
              <p className="text-gray-400">Your transaction history will appear here</p>
            </GlassCard>
          )}
        </TabsContent>

        <TabsContent value="withdrawals" className="mt-6">
          {withdrawals.length > 0 ? (
            <GlassCard className="overflow-hidden p-0">
              <Table>
                <TableHeader>
                  <TableRow className="border-gray-100">
                    <TableHead className="text-gray-500 font-semibold">Amount</TableHead>
                    <TableHead className="text-gray-500 font-semibold">Bank Details</TableHead>
                    <TableHead className="text-gray-500 font-semibold">Status</TableHead>
                    <TableHead className="text-gray-500 font-semibold">Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {withdrawals.map((w) => (
                    <TableRow key={w.id} className="border-gray-50">
                      <TableCell className="text-[#4E50FF] font-mono font-semibold">{formatCurrency(w.amount)}</TableCell>
                      <TableCell>
                        <div>
                          <p className="text-gray-900">{w.bank_name}</p>
                          <p className="text-sm text-gray-500">{w.bank_account}</p>
                        </div>
                      </TableCell>
                      <TableCell><StatusBadge status={w.status} /></TableCell>
                      <TableCell className="text-gray-500">{formatDateTime(w.created_at)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </GlassCard>
          ) : (
            <GlassCard className="text-center py-12">
              <Banknote className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Withdrawals</h3>
              <p className="text-gray-400">Your withdrawal history will appear here</p>
            </GlassCard>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Wallet;
